import { useQuery, useMutation } from "@tanstack/react-query";
import {
  queryClient,
  apiRequest,
  setAuthToken as setGlobalAuthToken,
  getQueryFn,
} from "@/lib/queryClient";
import { useStore } from "@/lib/store";
import { useEffect } from "react";
import i18n, { getBaseLanguage, setAppLanguage, type SupportedLanguage } from "@/i18n";
import { posthog } from "@/lib/posthog";
import { trackReferralSignup } from "@/lib/firstpromoter";
import { pushAccountCreated } from "@/lib/gtm";
import type { BillingProvider } from "@shared/billing-provider";

type User = {
  id: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  tokensRemaining?: number;
  tokensUsedThisMonth?: number;
  currentMonthYear?: string;
  role?: string;
  agencyId?: string | null;
  agencyName?: string | null;
  agencyPermissions?: string[] | null;
  agencyRolePreset?: string | null;
  permissionMode?: string | null;
  allowedModules?: string[] | null;
  hiddenModules?: string[] | null;
  preferredCurrency?: string | null;
  hasSeenDemoPromptEarly?: boolean;
  hasSeenDemoPromptMid?: boolean;
  trialAiResponseCount?: number;
  trialEndsAt?: string | null;
  subscriptionActive?: boolean;
  subscriptionPlanKey?: string | null;
  /**
   * VP-116: rozwiązany po stronie serwera dostęp do Performance — z dziedziczeniem planu
   * po właścicielu agencji i bypassem super_admina. Używaj TEGO pola do gatingu, nie
   * parsuj `subscriptionPlanKey` — ten trzyma własny klucz usera, który u pracownika
   * agencji bywa pusty i dawał fail-open przy backendzie odrzucającym każde żądanie.
   */
  hasPerformance?: boolean;
  /**
   * Dostęp do modułu Shopify rozwiązany serwerowo (super_admin / whitelista testerów /
   * konto z zainstalowanym sklepem). Gatuj karty przez `canAccessShopifyIntegration(user)`.
   */
  shopifyAccess?: boolean;
  /**
   * VP-329: czym konto jest rozliczane (`stripe` | `shopify` | `manual`). Merchant
   * z Shopify App Store NIE MOŻE zobaczyć niczego ze Stripe'a (wymóg App Store §1.2.1)
   * — gatuj przez `useBillingProvider()`, nie przez „ma sklep Shopify".
   */
  billingProvider?: BillingProvider;
  trialPlus?: boolean;
  pastDue?: boolean;
  paymentFailedAt?: string | null;
  locale?: SupportedLanguage | null;
  // Pola doliczane przez GET /api/auth/me (apps/api/routes.ts) ponad to, co siedzi w sesji:
  onboardingCompletedAt?: string | null;
  createdAt?: string | null;
  monthlyTokenAllocation?: number;
  adAccountPackSize?: number;
  stripeCustomerId?: string | null;
  intercomHash?: string;
  accessBlocked?: boolean;
  watermarkPreviewMode?: boolean;
};

/** Odpowiedź GET /api/auth/me — `user: null` = brak/wygasła sesja (VP-315). */
type MeResponse = { user: User | null };

interface AuthResponse {
  user: User;
  token: string;
  requires2FA?: boolean;
}

/** Thrown when login succeeds but TOTP code is required. */
export class TwoFactorRequiredError extends Error {
  constructor() {
    super("2FA_REQUIRED");
    this.name = "TwoFactorRequiredError";
  }
}

export function useAuth() {
  const { authToken, setAuthToken, clearAuth } = useStore();

  // Sync global auth token whenever it changes (including after zustand rehydration)
  useEffect(() => {
    if (authToken) {
      setGlobalAuthToken(authToken);
    }
  }, [authToken]);

  const {
    data: user,
    isLoading,
    isFetching,
    error,
  } = useQuery<MeResponse>({
    queryKey: ["/api/auth/me"],
    enabled: !!authToken,
    // Hard 10s ceiling on /api/auth/me. If server doesn't respond in 10s the
    // request aborts and the UI falls back to login instead of an infinite spinner.
    queryFn: getQueryFn({ on401: "throw", timeoutMs: 10_000 }),
    // Retry on transient backend failures (e.g. Supabase pooler timeouts) but
    // never on real 401 (loop), nor on TimeoutError (server is overloaded — backoff via UI).
    retry: (failureCount, err) => {
      if (!(err instanceof Error)) return failureCount < 1;
      if (err.message.startsWith("401:")) return false;
      if (err.name === "TimeoutError" || err.message.includes("aborted")) return false;
      return failureCount < 1;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    staleTime: 0, // Always refetch user data when requested
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes (gcTime is the new name for cacheTime)
    refetchOnWindowFocus: false, // Prevent loading flicker when server is down
  });

  // Clear auth on 401 (invalid token) or when server is unreachable after all retries.
  // Without clearing on timeout/network errors, users get stuck in a loading loop:
  // server down → timeout → isLoading:false shows login → window focus → refetch → repeat.
  // VP-315: wygasła/nieważna sesja przychodzi jako 200 { user: null } (nie 401), żeby
  // konsola przeglądarki nie świeciła czerwonym błędem przy każdym starcie ze starym tokenem.
  // `!isFetching`: nie działaj na starym wpisie z cache (np. anonimowy fetch sprzed hydratacji
  // tokena), dopóki trwa refetch z aktualnym tokenem.
  const sessionRejected = !isFetching && user !== undefined && user.user === null;
  useEffect(() => {
    if (!error && !sessionRejected) return;
    const msg = error?.message ?? "";
    const isAuthError = sessionRejected || msg.startsWith("401:");
    const isUnreachable =
      error?.name === "TimeoutError" || msg.includes("aborted") || msg.includes("Failed to fetch");
    if (isAuthError || isUnreachable) {
      clearAuth();
      setGlobalAuthToken(null);
      queryClient.clear();
    }
  }, [error, sessionRejected, clearAuth]);

  // Sync UI + AI language with the user's saved DB locale. The DB is the source
  // of truth — header/localStorage are only fallbacks for anonymous users.
  const dbLocale = user?.user?.locale ?? null;
  useEffect(() => {
    if (!dbLocale) return;
    if (getBaseLanguage(i18n.language) !== dbLocale) {
      void setAppLanguage(dbLocale);
    }
  }, [dbLocale]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string; totpCode?: string }) => {
      // 12-second timeout for login — DB lookup + bcrypt should never need more.
      // Server-side watchdog returns 503 at 9s, so client gives a small buffer.
      const signal = AbortSignal.timeout(12000);
      const response = await apiRequest("POST", "/api/auth/login", credentials, { signal });
      const data = response as AuthResponse;
      if (data.requires2FA) {
        throw new TwoFactorRequiredError();
      }
      return data;
    },
    onSuccess: (data) => {
      setAuthToken(data.token);
      setGlobalAuthToken(data.token);
      queryClient.setQueryData(["/api/auth/me"], { user: data.user });
      // Invalidate all queries EXCEPT /api/auth/me (already set above via setQueryData)
      // Invalidating /api/auth/me causes a race condition where the refetch fires
      // before the global auth token is propagated, resulting in a 401.
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== "/api/auth/me",
      });

      if (typeof window !== "undefined" && posthog) {
        posthog.identify(data.user.id, {
          email: data.user.email,
          name: data.user.username,
          role: data.user.role ?? null,
          agency_id: data.user.agencyId ?? null,
          plan_key: data.user.subscriptionPlanKey ?? null,
          subscription_active: data.user.subscriptionActive ?? false,
        });
        if (data.user.agencyId) {
          posthog.group("agency", data.user.agencyId, {
            name: data.user.agencyName ?? data.user.agencyId,
          });
        }
        posthog.capture("user_login", { method: "email_password" });
      }

      // User authentication successful - no automatic marking as seen
      // The welcome form will be marked as seen only when survey is completed via WelcomeModal
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: {
      username: string;
      email: string;
      password: string;
      trialType?: "standard" | "extended";
      referralCode?: string;
    }) => {
      const response = await apiRequest("POST", "/api/auth/register", userData);
      return response as AuthResponse;
    },
    onSuccess: (data) => {
      setAuthToken(data.token);
      setGlobalAuthToken(data.token);
      queryClient.setQueryData(["/api/auth/me"], { user: data.user });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== "/api/auth/me",
      });

      if (typeof window !== "undefined" && posthog) {
        posthog.identify(data.user.id, {
          email: data.user.email,
          name: data.user.username,
          signup_method: "email_password",
        });
        posthog.capture("user_register", { method: "email_password" });
      }

      // Track referral signup in FirstPromoter
      trackReferralSignup(data.user.email);

      pushAccountCreated({
        userId: data.user.id,
        email: data.user.email,
        signupMethod: "email_password",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      if (authToken) {
        await apiRequest("POST", "/api/auth/logout");
      }
    },
    onSuccess: () => {
      if (typeof window !== "undefined" && posthog) {
        posthog.capture("user_logout");
        posthog.reset();
      }

      clearAuth();
      setGlobalAuthToken(null);
      queryClient.clear();
    },
  });

  return {
    user: user?.user || null,
    isLoading,
    isAuthenticated: !!user?.user && !!authToken,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    isLoginLoading: loginMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}
